import { datadogRum } from '@datadog/browser-rum';
import { reactive } from 'vue';

import DividaDto from '@/models/dto/DividaDto';
import SincronizacaoDto from '@/models/dto/SincronizacaoDto';
import WlSincronizacaoDto from '@/models/dto/WlSincronizacaoDto';
import HttpStatusCode from '@/models/enums/HttpStatusCode';
import { DividaResource } from '@/models/resources';
import { ApiResponse, ApiResponseMetadata } from '@/models/resources/ApiResponse';
import SincronizacaoResource from '@/models/resources/SincronizacaoResource';
import {
  acertoDividaSincronizadaErro,
  acertoDividaSincronizadaSucesso,
  acertoDividaSincronizouComErroUnicoEndpointCredor,
  acertoDividaSincronizouErroUnicoEndpoint,
  acertoDividaSincronizouSucessoUnicoEndpoint,
} from '@/modules/events/acerto';
import { getRouter } from '@/router/indexRouter';

import convivenciaWlService from './ConvivenciaWlSevice';
import HttpService from './HttpService';
import propostaService from './PropostaService';
import sessionService from './SessionService';
import storageService from './StorageService';

class DividaService extends HttpService {
  private syncController: AbortController | null = null;

  public cancelarSincronizacoesEmAndamento(): void {
    if (this.syncController) {
      this.syncController.abort();
      this.syncController = null;
    }
  }

  /**
   * Sincroniza propostas de um credor em background
   */
  private static sincronizarPropostasDoCredor(api: string): void {
    propostaService.sincronizar(api).catch(() => {
      // Ignora erros de sincronização de propostas
    });
  }

  private static getApiFromCredor(credor: string): string | null {
    const apis = sessionService.token?.apis || [];
    if (apis.includes(credor)) {
      return credor;
    }
    const apiComMc = `mc-${credor}`;
    return apis.includes(apiComMc) ? apiComMc : null;
  }

  /**
   * Sincroniza credores do multicredor individualmente como fallback
   */
  private async sincronizarCredoresMCIndividual(
    apisMC: string[],
    processarChunk: (chunk: SincronizacaoResource, api?: string) => void,
    signal?: AbortSignal,
  ): Promise<{
    todasDividas: DividaResource[];
    sincronizouDividasPorApi: Record<string, boolean>;
  }> {
    const todasDividas: DividaResource[] = [];
    const sincronizouDividasPorApi: Record<string, boolean> = {};

    try {
      // Sincroniza cada credor do MC individualmente em paralelo
      const resultados = await Promise.allSettled(
        apisMC.map(async (api) => {
          try {
            const resultado = await this.sincronizar(api, signal);
            return { api, resultado };
          } catch (error) {
            acertoDividaSincronizadaErro(api);
            logger.warn(`Erro ao sincronizar credor ${api} individualmente:`, error);
            return { api, resultado: undefined };
          }
        }),
      );

      resultados.forEach((resultado) => {
        if (signal?.aborted) {
          return;
        }
        if (resultado.status === 'fulfilled' && resultado.value.resultado) {
          const { api, resultado: syncResult } = resultado.value;
          const novasDividas = syncResult.dividas as unknown as DividaResource[];

          todasDividas.push(...novasDividas);
          sincronizouDividasPorApi[api] = syncResult.sincronizouDividas;

          if (syncResult.sincronizouDividas) {
            // Sincroniza propostas imediatamente em background
            if (!signal?.aborted) {
              DividaService.sincronizarPropostasDoCredor(api);
            }
          }

          if (!signal?.aborted) {
            processarChunk(syncResult, api);
          }
        }
      });

      return {
        todasDividas,
        sincronizouDividasPorApi,
      };
    } catch (error) {
      logger.warn('Erro ao sincronizar credores MC individualmente:', error);
      throw error;
    }
  }

  private static lancarSeAbortada(signal?: AbortSignal): void {
    if (signal?.aborted) {
      const err = new Error('Sincronização cancelada');
      err.name = 'AbortError';
      throw err;
    }
  }

  /**
   * Faz requisições reais e retorna os dados completos
   * Chamado pelo useDividasQueries que gerencia o cache
   */
  public async sincronizarDividasViaAPI(
    onChunk: (chunk: SincronizacaoResource, veioDoCache?: boolean, api?: string) => void,
    externalSignal?: AbortSignal,
  ): Promise<{
    todasDividas: DividaResource[];
    sincronizouDividasPorApi: Record<string, boolean>;
    deveSincronizarPropostasPorApi: Record<string, boolean>;
    credoresSincronizados: string[];
  }> {
    this.cancelarSincronizacoesEmAndamento();
    const controller = new AbortController();
    this.syncController = controller;

    const removeExternalAbortListener = DividaService
      .linkExternalAbortSignal(externalSignal, controller);

    const apis = sessionService.token?.apis || [];
    const apisWhiteLabel = apis.filter((api) => convivenciaWlService.isWhiteLabel(api));
    const apisMC = apis.filter((api) => !convivenciaWlService.isWhiteLabel(api));

    const todasDividas: DividaResource[] = [];
    const sincronizouDividasPorApi: Record<string, boolean> = {};
    const deveSincronizarPropostasPorApi: Record<string, boolean> = {};
    const credoresSincronizados: string[] = [];

    const processarChunk = (chunk: SincronizacaoResource, api?: string): void => {
      const novasDividas = chunk.dividas as unknown as DividaResource[];
      todasDividas.push(...novasDividas);

      // Rastreia sincronizouDividas por API
      const apiDoRetorno = novasDividas[0]?.api ?? api;
      if (apiDoRetorno) {
        sincronizouDividasPorApi[apiDoRetorno] = chunk.sincronizouDividas;
      }

      queueMicrotask(() => {
        if (controller.signal.aborted) return;
        onChunk(chunk, false, apiDoRetorno);
      });
    };

    const streamingPromise = this.sincronizarStreamingMC(processarChunk, controller.signal);
    const whitelabelPromises = apisWhiteLabel.map(
      async (api) => this.sincronizarWhitelabelApi(api, processarChunk, controller.signal),
    );

    try {
      const [resultadoStreaming] = await Promise.allSettled([
        streamingPromise,
        Promise.allSettled(whitelabelPromises),
      ]);

      DividaService.lancarSeAbortada(controller.signal);

      // Se o streaming falhou, faz fallback para sincronização individual
      if (resultadoStreaming.status === 'rejected') {
        logger.warn('Erro no streaming do multicredor, fazendo fallback para sincronização individual:', resultadoStreaming.reason);

        DividaService.lancarSeAbortada(controller.signal);

        const resultadoFallback = await this.sincronizarCredoresMCIndividual(
          apisMC,
          processarChunk,
          controller.signal,
        );

        todasDividas.push(...resultadoFallback.todasDividas);
        Object.assign(sincronizouDividasPorApi, resultadoFallback.sincronizouDividasPorApi);
      } else if (resultadoStreaming.status === 'fulfilled') {
        // Processa informações dos credores sincronizados do streaming
        resultadoStreaming.value.credoresSincronizados.forEach(
          ({ api, deveSincronizarPropostas }) => {
            if (api) {
              sincronizouDividasPorApi[api] = true;
              deveSincronizarPropostasPorApi[api] = deveSincronizarPropostas;
              credoresSincronizados.push(api);
            }
          },
        );
      }

      return {
        todasDividas,
        sincronizouDividasPorApi,
        deveSincronizarPropostasPorApi,
        credoresSincronizados,
      };
    } finally {
      removeExternalAbortListener?.();
      if (this.syncController === controller) {
        this.syncController = null;
      }
    }
  }

  private static linkExternalAbortSignal(
    externalSignal: AbortSignal | undefined,
    controller: AbortController,
  ): (() => void) {
    let removeExternalAbortListener: (() => void) = () => {};
    if (externalSignal) {
      if (externalSignal.aborted) {
        controller.abort();
      } else {
        const onExternalAbort = (): void => { controller.abort(); };
        externalSignal.addEventListener('abort', onExternalAbort, { once: true });
        removeExternalAbortListener = () => { externalSignal.removeEventListener('abort', onExternalAbort); };
      }
    }
    return removeExternalAbortListener;
  }

  public async sincronizar(api: string, signal?: AbortSignal)
    : Promise<SincronizacaoResource> {
    datadogRum.addAction('sys:divida_service:sincronizar', { api });
    const url = `/api/${api}/Consumidor/Dividas/Sincronizar`;
    const iniciSincronizacao = Date.now();

    const response = signal
      ? await this.post<ApiResponseMetadata<SincronizacaoDto>>(url, undefined, { signal })
      : await this.post<ApiResponseMetadata<SincronizacaoDto>>(url);

    const result = new SincronizacaoResource(response.data);

    if (result.sincronizouDividas) {
      const latencias = this.CalcularLatencias(iniciSincronizacao, response);
      acertoDividaSincronizadaSucesso(api, result.dividas, latencias);
    }

    return result;
  }

  public async listarDividasWl(api: string, signal?: AbortSignal): Promise<WlSincronizacaoDto> {
    datadogRum.addAction('sys:divida_service:listar_dividas', { api });
    const url = `/api/${api}/Consumidor/Dividas`;
    const response = signal
      ? await this.get<ApiResponse<WlSincronizacaoDto>>(url, { signal })
      : await this.get<ApiResponse<WlSincronizacaoDto>>(url);
    return response.data;
  }

  public async obterDivida(
    api: string,
    dividaId: number,
  ): Promise<DividaResource> {
    datadogRum.addAction('sys:divida_service:obter_divida', { api, dividaId });
    const response = await this.get<ApiResponse<DividaDto>>(
      `/api/${api}/Consumidor/Dividas/${dividaId}`,
    );
    return new DividaResource(response.data);
  }

  /**
   * Dívida completa após sincronização de ofertas (nível completo).
   */
  public async obterDividaOfertasCompletas(
    api: string,
    dividaId: number,
    signal?: AbortSignal,
  ): Promise<DividaResource> {
    datadogRum.addAction('sys:divida_service:obter_divida_ofertas_completas', { api, dividaId });
    const url = `/api/${api}/Consumidor/Dividas/${dividaId}/OfertasCompletas`;
    const response = signal
      ? await this.get<ApiResponse<DividaDto>>(url, { signal })
      : await this.get<ApiResponse<DividaDto>>(url);
    return new DividaResource(response.data);
  }

  /**
   * Sincroniza todas as dívidas do multicredor usando streaming HTTP
   * Recebe chunks de dados e processa incrementalmente
   */
  // eslint-disable-next-line class-methods-use-this
  public async sincronizarStreamingMC(
    processarChunk: (chunk: SincronizacaoResource, api?: string) => void,
    signal?: AbortSignal,
  ): Promise<{
    resultado: SincronizacaoResource;
    credoresSincronizados: Array<{
      credor: string;
      api: string | null;
      deveSincronizarPropostas: boolean;
    }>;
  }> {
    datadogRum.addAction('sys:divida_service:sincronizar_streaming');
    const token = storageService.session.get();
    if (!token) {
      throw new Error('Token não encontrado');
    }

    const iniciSincronizacao = Date.now();
    const response = await fetch('/api/credores/Consumidor/Dividas/Sincronizar', {
      method: 'POST',
      signal,
      headers: {
        accept: 'application/json, text/plain, */*',
        'content-type': 'application/json',
        authorization: `Bearer ${token.rawToken}`,
        'cache-control': 'no-store',
        pragma: 'no-cache',
      },
    });

    if (!response.ok) {
      acertoDividaSincronizouErroUnicoEndpoint();

      if (response.status === HttpStatusCode.UNAUTHORIZED) {
        const router = getRouter();
        if (router) {
          router.push('/logout');
        }
        throw new Error('Sessão expirada');
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Stream não disponível');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    const todasDividas: DividaDto[] = [];
    const periodoIndisponibilidadePorApi: Record<string, SincronizacaoDto['periodoIndisponibilidade']> = {};
    let sincronizouDividas = true;
    const credoresSincronizados: Array<{
      credor: string;
      api: string | null;
      deveSincronizarPropostas: boolean;
    }> = [];

    interface SincronizacaoStreamDto extends SincronizacaoDto {
      credor: string;
      deveSincronizarPropostas: boolean;
    }

    const processarLinha = (linha: string): void => {
      try {
        if (signal?.aborted) {
          return;
        }
        const apiResponse = JSON.parse(linha) as ApiResponseMetadata<SincronizacaoStreamDto>;
        if (!apiResponse.isValid) {
          logger.warn('Chunk inválido recebido:', apiResponse.errors);
          return;
        }

        const { data } = apiResponse;
        const {
          credor,
          dividas,
          periodoIndisponibilidade,
          sincronizouDividas: sincronizou,
          deveSincronizarPropostas,
        } = data;

        // Rastreia todos os credores que sincronizaram, mesmo sem dívidas
        if (sincronizou === true) {
          const api = DividaService.getApiFromCredor(credor);
          credoresSincronizados.push({
            credor,
            api,
            deveSincronizarPropostas: deveSincronizarPropostas ?? false,
          });

          const latencias = this.CalcularLatencias(iniciSincronizacao, apiResponse);

          acertoDividaSincronizadaSucesso(
            `${api}`,
            dividas.map(d => new DividaResource(d)),
            latencias
          );
        }

        if (dividas?.length > 0) {
          todasDividas.push(...dividas);
        }

        if (periodoIndisponibilidade) {
          periodoIndisponibilidadePorApi[credor] = periodoIndisponibilidade;
        }

        if (sincronizou === false) {
          sincronizouDividas = false;
          acertoDividaSincronizouComErroUnicoEndpointCredor(credor);
        }

        if (!signal?.aborted) {
          const api = DividaService.getApiFromCredor(credor);
          if (api && deveSincronizarPropostas) {
            // Sincroniza propostas imediatamente em background
            DividaService.sincronizarPropostasDoCredor(api);
          } else if (api && sincronizou !== false) {
            propostaService.registrarPropostasAtualizadas(api);
          }
        }

        if (processarChunk && !signal?.aborted) {
          const api = DividaService.getApiFromCredor(credor) ?? undefined;
          processarChunk(new SincronizacaoResource({
            dividas,
            sincronizouDividas: sincronizou ?? true,
            periodoIndisponibilidade,
          }), api);
        }
      } catch (error) {
        logger.warn('Erro ao processar chunk:', error);
      }
    };

    try {
      let done = false;
      while (!done) {
        // eslint-disable-next-line no-await-in-loop
        const result = await reader.read();
        done = result.done;
        if (done) break;

        buffer += decoder.decode(result.value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        lines
          .filter((line) => line.trim())
          .forEach(processarLinha);
      }

      if (signal?.aborted) {
        const err = new Error('Sincronização cancelada');
        err.name = 'AbortError';
        throw err;
      }

      const periodoIndisponibilidadeAgregado = Object.values(periodoIndisponibilidadePorApi)
        .find((periodo) => periodo !== null && periodo !== undefined);

      // Dispara evento de sucesso para endpoint único
      acertoDividaSincronizouSucessoUnicoEndpoint();

      return {
        resultado: new SincronizacaoResource({
          dividas: todasDividas,
          sincronizouDividas,
          periodoIndisponibilidade: periodoIndisponibilidadeAgregado,
        }),
        credoresSincronizados,
      };
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') {
        throw error;
      }
      // Dispara evento de erro para endpoint único
      acertoDividaSincronizouErroUnicoEndpoint();
      throw error;
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Sincroniza dívidas de uma API whitelabel específica
   * e emite um chunk sintético via processarChunk.
   */
  public async sincronizarWhitelabelApi(
    api: string,
    processarChunk?: (chunk: SincronizacaoResource, api?: string) => void,
    signal?: AbortSignal,
  ): Promise<SincronizacaoResource> {
    const iniciSincronizacao = Date.now();
    try {
      // Primeiro sincroniza antes de listar as dívidas
      datadogRum.addAction('sys:divida_service:sincronizar', { api });
      const url = `/api/${api}/Consumidor/Dividas/Sincronizar`;

      signal
        ? await this.post(url, undefined, { signal })
        : await this.post(url);

      if (signal?.aborted) {
        return new SincronizacaoResource({
          dividas: [],
          sincronizouDividas: false,
          periodoIndisponibilidade: undefined,
        });
      }

      const wlDto = await this.listarDividasWl(api, signal);
      const latencias = {
        rede: undefined,
        total: Date.now() - iniciSincronizacao,
      };
      
      
      if (!wlDto || signal?.aborted) {
        return new SincronizacaoResource({
          dividas: [],
          sincronizouDividas: false,
          periodoIndisponibilidade: undefined,
        });
      }
      const dividasNegociaveis = await convivenciaWlService
        .filtrarNegociaveisEAtualizarStorePropostas(api, wlDto);
      propostaService.registrarPropostasAtualizadas(api);

      // Dispara evento de sucesso para whitelabel
      acertoDividaSincronizadaSucesso(api, dividasNegociaveis, latencias);

      const chunk = new SincronizacaoResource({
        dividas: dividasNegociaveis as unknown as DividaDto[],
        sincronizouDividas: true,
        periodoIndisponibilidade: undefined,
      });

      if (processarChunk) {
        // Usa queueMicrotask para garantir que o Vue reaja imediatamente
        // mesmo quando chamado dentro de uma Promise assíncrona
        queueMicrotask(() => {
          if (signal?.aborted) return;
          processarChunk(chunk, api);
        });
      }

      return chunk;
    } catch (error) {
      // Dispara evento de erro para whitelabel
      const errorObj = error as {
        response?: { status?: number; statusText?: string };
        message?: string;
      };
      acertoDividaSincronizadaErro(
        api,
        errorObj.response?.status,
        errorObj.response?.statusText,
        errorObj.message,
      );
      // Ignora erros individuais de whitelabel para não quebrar o fluxo geral
      return new SincronizacaoResource({
        dividas: [],
        sincronizouDividas: false,
        periodoIndisponibilidade: undefined,
      });
    }
  }

  private CalcularLatencias(
    iniciSincronizacao: number,
    response: ApiResponseMetadata<SincronizacaoDto>
  ): { rede?: number; total: number } {
    try {
      return {
        rede: Date.now() - iniciSincronizacao - response.metadata.duration,
        total: Date.now() - iniciSincronizacao,
      };
    } catch {
      return {
        rede: undefined,
        total: Date.now() - iniciSincronizacao,
      };
    }
  }
}

const dividaService = new DividaService();
export default reactive(dividaService);
